import React, { createContext, useContext, ReactNode } from 'react'
import {
  normalizeLanguage,
  useTranslations as useLocaleTranslations,
} from '@chilipiper/locale/src/new'
import { PublicLocalizationEntry } from '@chilipiper/api-client/src/customization-settings'
import { useTranslationsWithEnglishFallback } from '../hooks/useTranslations'

type CustomTranslations = Record<string, string>

type TranslationContextType = {
  customTranslations: CustomTranslations
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined)

type TranslationProviderProps = {
  children: ReactNode
  tenantId: string
  workspaceId: string
  overrideCustomTranslations?: CustomTranslations
}

const extractCustomTranslations = (translations: PublicLocalizationEntry[]): CustomTranslations => {
  return translations.reduce<CustomTranslations>((acc, translation) => {
    if (translation.key.key === 'Chat') {
      acc[translation.key.subKey] = translation.value
    }
    return acc
  }, {})
}

export const TranslationProvider = ({
  children,
  tenantId,
  workspaceId,
  overrideCustomTranslations,
}: TranslationProviderProps) => {
  const locale = normalizeLanguage(navigator.language)
  const translationsWithEnglishFallback = useTranslationsWithEnglishFallback(
    tenantId,
    workspaceId,
    locale
  )

  const baseTranslations = extractCustomTranslations(translationsWithEnglishFallback)

  const customTranslations = { ...baseTranslations, ...(overrideCustomTranslations ?? {}) }

  return (
    <TranslationContext.Provider value={{ customTranslations }}>
      {children}
    </TranslationContext.Provider>
  )
}

export const useTranslations = (keys: string[]) => {
  const { customTranslations } = useTranslationContext()
  const translations = useLocaleTranslations(keys)
  return keys.map((key, index) => customTranslations[key] ?? translations[index])
}

const useTranslationContext = (): TranslationContextType => {
  const context = useContext(TranslationContext)
  if (!context) {
    throw new Error('useTranslationContext must be used within a TranslationProvider')
  }
  return context
}
